import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useEntitlementOverride } from "@/lib/entitlementFlip";

export interface EntitlementInfo {
  tier: string | null;
  source: string | null;
  /** ISO string, or null for admin comp / admin-preview rows with no expiry. */
  expiresAt: string | null;
  /** Present only for the Annual (subscription) plan; null for Founding. */
  subscriptionId: string | null;
  loaded: boolean;
}

const EMPTY: EntitlementInfo = {
  tier: null,
  source: null,
  expiresAt: null,
  subscriptionId: null,
  loaded: false,
};

/**
 * Reads the signed-in user's row in the `entitlements` table for DISPLAY
 * purposes only (the Settings Pro badge). Gating still runs through
 * useAuth().isPro / useIsPro() — this hook adds nothing to the entitlement
 * decision, it just surfaces the columns the badge needs to show a renewal
 * line (source + expires_at + whether a Stripe subscription exists).
 *
 * Re-fetches when the user changes AND whenever the entitlement override
 * flips (EntitlementFlipListener → fireEntitlementFlipped), so a live
 * free→pro flip populates the date line without an app refresh. Fails soft:
 * any error just leaves the info empty (no date line renders).
 */
export const useEntitlement = (): EntitlementInfo => {
  const { user } = useAuth();
  // Subscribing to the override makes this hook re-run its effect the moment a
  // free→pro flip is detected mid-session (same signal useIsPro listens to).
  const override = useEntitlementOverride();
  const [info, setInfo] = useState<EntitlementInfo>(EMPTY);

  useEffect(() => {
    if (!user?.id) {
      setInfo({ ...EMPTY, loaded: true });
      return;
    }
    let cancelled = false;
    setInfo((prev) => ({ ...prev, loaded: false }));
    (async () => {
      try {
        const { data } = await supabase
          .from("entitlements")
          .select("tier, source, expires_at, stripe_subscription_id")
          .eq("user_id", user.id)
          .maybeSingle();
        if (cancelled) return;
        const row = (data ?? null) as {
          tier?: string | null;
          source?: string | null;
          expires_at?: string | null;
          stripe_subscription_id?: string | null;
        } | null;
        setInfo({
          tier: row?.tier ?? null,
          source: row?.source ?? null,
          expiresAt: row?.expires_at ?? null,
          subscriptionId: row?.stripe_subscription_id ?? null,
          loaded: true,
        });
      } catch {
        if (cancelled) return;
        setInfo({ ...EMPTY, loaded: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, override]);

  return info;
};
